import React, { useRef, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
} from '@mui/material';
import { GET_CONTRACT_LOCATIONS } from '../queries/map';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

interface LandPlot {
  id: number;
  address_text?: string;
  latitude?: number;
  longitude?: number;
}

interface Contract {
  id: number;
  status: string;
  market_day: string;
  buyer_organisation?: { id: number; name: string };
  seller_transactor?: {
    id: number;
    person?: { id: number; full_name: string };
  };
  harvest_price_contract_bundles?: Array<{
    id: number;
    harvest_bundle?: {
      id: number;
      land_plot?: LandPlot;
    };
  }>;
}

const MapPage: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapStyle, setMapStyle] = useState<string>('mapbox://styles/mapbox/light-v11');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  const { data, loading, error } = useQuery(GET_CONTRACT_LOCATIONS, {
    variables: { filter: {} },
  });

  const contracts: Contract[] = (data as any)?.palmoil_transport_crew_contract || [];

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      zoom: 8,
      center: [101.4478, 0.5071], // Sumatra, Indonesia
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map style
  useEffect(() => {
    if (map.current) {
      map.current.setStyle(mapStyle);
    }
  }, [mapStyle]);

  // Add markers for contracts
  useEffect(() => {
    if (!map.current || !contracts.length) return;

    // Clear existing markers
    markers.current.forEach((marker) => marker.remove());
    markers.current = [];

    const bounds = new mapboxgl.LngLatBounds();
    let hasValidLocation = false;

    contracts.forEach((contract) => {
      contract.harvest_price_contract_bundles?.forEach((bundle) => {
        const plot = bundle.harvest_bundle?.land_plot;
        if (plot?.latitude && plot?.longitude) {
          hasValidLocation = true;

          // Create marker element
          const el = document.createElement('div');
          el.className = 'custom-marker';
          el.style.backgroundColor = contract.status === 'published' ? '#4caf50' : '#ff9800';
          el.style.width = '20px';
          el.style.height = '20px';
          el.style.borderRadius = '50%';
          el.style.border = '2px solid white';
          el.style.cursor = 'pointer';
          el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

          // Create popup
          const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 8px;">
              <strong>Contract #${contract.id}</strong><br/>
              <span style="color: ${contract.status === 'published' ? '#4caf50' : '#ff9800'}">
                ${contract.status.toUpperCase()}
              </span><br/>
              <strong>Buyer:</strong> ${contract.buyer_organisation?.name || 'N/A'}<br/>
              <strong>Seller:</strong> ${contract.seller_transactor?.person?.full_name || 'N/A'}<br/>
              <strong>Location:</strong> ${plot.address_text || 'No address'}<br/>
              <a href="/contracts/${contract.id}" style="color: #1976d2; text-decoration: none;">
                View Details →
              </a>
            </div>
          `);

          // Create marker
          const marker = new mapboxgl.Marker(el)
            .setLngLat([plot.longitude, plot.latitude])
            .setPopup(popup)
            .addTo(map.current!);

          el.addEventListener('click', () => {
            setSelectedContract(contract);
          });

          markers.current.push(marker);
          bounds.extend([plot.longitude, plot.latitude]);
        }
      });
    });

    // Fit map to bounds if we have valid locations
    if (hasValidLocation && map.current) {
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
    }
  }, [contracts]);

  if (error) {
    return (
      <Box>
        <Alert severity="error">Error loading map data: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Map</Typography>
        <ToggleButtonGroup
          value={mapStyle}
          exclusive
          onChange={(_, newStyle) => newStyle && setMapStyle(newStyle)}
          size="small"
        >
          <ToggleButton value="mapbox://styles/mapbox/light-v11">Light</ToggleButton>
          <ToggleButton value="mapbox://styles/mapbox/dark-v11">Dark</ToggleButton>
          <ToggleButton value="mapbox://styles/mapbox/satellite-streets-v12">Satellite</ToggleButton>
          <ToggleButton value="mapbox://styles/mapbox/streets-v12">Streets</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 200px)' }}>
        {/* Map Container */}
        <Paper sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        </Paper>

        {/* Contract Details Sidebar */}
        {selectedContract && (
          <Paper sx={{ width: 300, p: 2, overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Contract #{selectedContract.id}
            </Typography>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedContract.status.toUpperCase()}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Buyer
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedContract.buyer_organisation?.name || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Seller
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedContract.seller_transactor?.person?.full_name || 'N/A'}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Market Day
                </Typography>
                <Typography variant="body1">
                  {selectedContract.market_day || 'N/A'}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="subtitle2" gutterBottom>
              Land Plots ({selectedContract.harvest_price_contract_bundles?.length || 0})
            </Typography>
            {selectedContract.harvest_price_contract_bundles?.map((bundle) => {
              const plot = bundle.harvest_bundle?.land_plot;
              return plot ? (
                <Card key={bundle.id} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2">
                      {plot.address_text || `Land Plot #${plot.id}`}
                    </Typography>
                    {plot.latitude && plot.longitude && (
                      <Typography variant="caption" color="text.secondary">
                        {plot.latitude.toFixed(4)}, {plot.longitude.toFixed(4)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ) : null;
            })}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default MapPage;
